/**
 * The session guard — the "logged out but stuck inside the shell" fix. Route `beforeLoad` guards
 * (routes.tsx) run only on NAVIGATION; the heartbeat's `refresh()` can find a session dead while
 * the shell just sits there (the PO left a tab open past the 12 h absolute cap), flip the store,
 * and nothing moves. This subscribes to the auth store and reacts to the two store transitions a
 * dead session produces:
 *
 *  1. status `authenticated` → `unauthenticated` (the IDENTITY is gone: an expired/revoked
 *     session, or a sign-out from any call site) while NOT on an auth route → /login, carrying the
 *     current href as `redirect` exactly like the `_authenticated` guard does (honoured only for
 *     an allowlisted return path — router/redirect.ts).
 *  2. the FIRM principal going null while still authenticated and on a firm route (anything under
 *     the app shell) — worker-api refused the firm session on the heartbeat's `refresh()` → ONE
 *     silent `reselectFirm()` for the firm that WAS bound: true → reset the query cache so the
 *     mounted screens refetch in place (no navigation); false → /firms; a 401 (the identity is
 *     dead too) → sign out, which is transition 1 and lands on /login.
 *
 * Deliberately NOT reacted to: `loading` → `unauthenticated` (a cold start, owned by the route
 * guards, which also redirect — reacting would navigate twice), and an unbind the SPA started
 * itself — `selectFirm`'s fail-closed unbind (the sidebar firm switcher selects in place from a
 * firm route) and `dropFirm` — which raise the store's `_firmChangeInFlight`. The query-failure
 * path (a card's 401/404) belongs to the central handler (lib/query-client.ts); where both fire
 * for one loss they navigate to the same href, which the router treats as a reload, not a second
 * history entry.
 */
import { isApiError } from '../lib/api.ts';
import { useAuth, type Firm } from '../store/auth-store.ts';
import type { createAppRouter } from './router.tsx';

type AppRouter = ReturnType<typeof createAppRouter>;

/** The sign-in / recovery surfaces that need no session — a dead session there is not news. */
export function isAuthRoute(pathname: string): boolean {
  return (
    pathname === '/login' ||
    pathname.startsWith('/login/') ||
    pathname === '/forgot-password' ||
    pathname.startsWith('/reset-password/') ||
    pathname.startsWith('/invite/')
  );
}

/** A screen under the firm-bound app shell: everything but the auth routes, the picker and `/`. */
export function isFirmRoute(pathname: string): boolean {
  return !isAuthRoute(pathname) && pathname !== '/firms' && pathname !== '/';
}

/**
 * Subscribe the guard to `store` (the app's `useAuth` unless a test injects one) and drive
 * `router`. Returns the unsubscribe — tests MUST call it, the store is a module singleton.
 */
export function installSessionGuard(
  router: AppRouter,
  store: typeof useAuth = useAuth,
): () => void {
  async function recoverFirm(previous: Firm | null): Promise<void> {
    let rebound = false;
    try {
      rebound = await store.getState().reselectFirm(previous);
    } catch (err) {
      if (isApiError(err) && err.status === 401) {
        // The identity is dead too. Signing out flips status, which transition 1 routes to /login.
        await store.getState().signOut();
        return;
      }
      // Inconclusive (unreachable): fall through to the picker, which can retry on demand.
    }
    if (rebound) {
      // Same firm, fresh session: drop every cached response and refetch what is on screen.
      await router.options.context?.queryClient.resetQueries();
      return;
    }
    void router.navigate({ to: '/firms', replace: true });
  }

  return store.subscribe((state, prev) => {
    // Where the app IS (or is heading, mid-navigation) — not the last resolved location.
    const location = router.latestLocation;

    if (prev.status === 'authenticated' && state.status === 'unauthenticated') {
      if (isAuthRoute(location.pathname)) return;
      void router.navigate({ to: '/login', search: { redirect: location.href }, replace: true });
      return;
    }

    if (
      state.status === 'authenticated' &&
      prev.user !== null &&
      state.user === null &&
      !state._firmChangeInFlight &&
      isFirmRoute(location.pathname)
    ) {
      void recoverFirm(prev.firm);
    }
  });
}
